/** @flow */

import React, { Component } from "react";
import { Button } from "../button";
import { GSGrid, GSRenderers } from "@components";
import { SectionComponent } from "../section";
import Icons from "../icons";
import "./change-log.style.scss";

type ChangeLine = $Exact<{
  id: number,
  operation: string,
  title: string,
  from: string,
  value: string,
}>;

type ChangeSource = "SIP" | "USER" | "SYSTEM" | "THIRD_PARTY_APP" | null;

export type LogEntry = $Exact<{
  id: number,
  operation: string,
  label: string,
  description: string,
  updatedAt: string,
  updatedBy: $Exact<{
    firstName: string,
    id: number,
    lastName: string,
    changeSource?: ChangeSource,
  }> | null,
  changes: Array<ChangeLine>,
}>;

type Props = $Exact<{
  entries: Array<LogEntry>,
  t: (name: string) => string,
  formatDate: (date: string) => string,
}>;

type State = $Exact<{
  expanded: Array<number>,
}>;

class ChangeLog extends Component<Props, State> {
  state = {
    expanded: [],
  };

  get changesGridConfig() {
    return [
      {
        key: "operation",
        name: this.props.t("CHANGE_LOG_COL_OPERATION"),
      },
      {
        key: "title",
        name: this.props.t("CHANGE_LOG_COL_PROPERTY"),
      },
      {
        key: "from",
        name: this.props.t("CHANGE_LOG_COL_FROM"),
      },
      {
        key: "value",
        name: this.props.t("CHANGE_LOG_COL_TO"),
      },
    ];
  }

  expand = (id: number) => () => {
    this.setState({
      expanded: this.state.expanded.includes(id)
        ? this.state.expanded.filter(item => item !== id)
        : [...this.state.expanded, id],
    });
  };

  expandAll = () => {
    this.setState({
      expanded:
        this.state.expanded.length === this.props.entries.length
          ? []
          : this.props.entries.map((_, index) => index + 1),
    });
  };

  get hasAnyChangesInLines(): boolean {
    return this.props.entries.some(entry => entry.changes);
  }

  render() {
    return (
      <SectionComponent
        title={<h6>{this.props.t("CHANGE_LOG")}</h6>}
        rightLink={
          this.hasAnyChangesInLines ? (
            <Button color="secondary" onClick={this.expandAll} size="small">
              {this.state.expanded.length === this.props.entries.length
                ? this.props.t("CHANGE_LOG_COLLAPSE_ALL")
                : this.props.t("CHANGE_LOG_EXPAND_ALL")}
            </Button>
          ) : null
        }
        className="gs-container"
      >
        <div className="change-log-container w-100">
          <div className="header-container clearfix">
            <div className="collapsing" />
            <div className="flexible-cols">
              <div className="cols">
                <div className="operation-col">{this.props.t("CHANGE_LOG_COL_OPERATION")}</div>
                <div className="entity-col">{this.props.t("CHANGE_LOG_COL_RECORD")}</div>
                <div className="updated-by-col">{this.props.t("CHANGE_LOG_COL_UPDATED_BY")}</div>
                <div className="updated-at-col">{this.props.t("CHANGE_LOG_COL_UPDATED_AT")}</div>
              </div>
            </div>
          </div>
          <div className="data-container">
            {this.props.entries.map((line, index) => (
              <div key={index + 1} className="change-log-entry clearfix">
                {line.changes ? (
                  <div className="collapsing cursor-pointer d-flex">
                    {this.state.expanded.includes(index + 1) ? (
                      <span className="material-icons pt-1" onClick={this.expand(index + 1)}>
                        expand_less
                      </span>
                    ) : (
                      <span className="material-icons pt-1" onClick={this.expand(index + 1)}>
                        expand_more
                      </span>
                    )}
                  </div>
                ) : null}
                <div className="flexible-cols">
                  <div className="cols clearfix">
                    <div className="operation-col">{line.description}</div>
                    <div className="entity-col">{line.label}</div>
                    <div className="updated-by-col">
                      {GSRenderers.updatedBy({ ...line, t: this.props.t })}
                    </div>
                    <div className="updated-at-col">{this.props.formatDate(line.updatedAt)}</div>
                  </div>
                  {this.state.expanded.includes(index + 1) && line.changes ? (
                    <div className="expanded-area pb-2">
                      <GSGrid columns={this.changesGridConfig} rows={line.changes} />
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionComponent>
    );
  }
}

export default ChangeLog;
